
'use client';

import { Header } from "@/components/landing/header";
import { Footer } from "@/components/landing/footer";
import Link from "next/link";
import Image from "next/image";
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { openingsData } from '@/lib/jobs-data';
import { ArrowDown, Globe, TrendingUp, Lightbulb, Handshake, Plane, Award, Search, MapPin, Clock, Code, ChevronRight } from 'lucide-react';

// Section 1: Hero
function Hero() {
  return (
    <section className="relative w-full min-h-[70vh] flex items-center justify-center pt-28 pb-12 overflow-hidden liquid-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 text-center">
            <div className="animate-fade-in-up flex flex-col items-center justify-center space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-panel-intense border border-white/20 text-white/90 text-sm font-medium mb-4">
                    <span className="w-2 h-2 rounded-full bg-neon-green animate-pulse"></span>
                    We are hiring
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-7xl font-extrabold leading-[1.1] tracking-tight drop-shadow-2xl text-white">
                    Build the <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-green to-neon-blue drop-shadow-sm">Future</span> with Us
                </h1>
                <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto leading-relaxed font-light tracking-wide">
                    Join a team where code meets creativity. At Darshni Software, we don't just fill positions; we fuel passions and engineer careers.
                </p>
            </div>
        </div>
    </section>
  );
}

// Section 2: Life at Darshni
function Life() {
  return (
    <section className="py-24 bg-background relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-grid-slate-200/50 dark:bg-grid-slate-800/50 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-10"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
            <div className="text-center mb-16">
                <h2 className="text-3xl md:text-5xl font-bold text-slate-800 dark:text-white mb-4">Life at <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-primary">Darshni</span></h2>
                <p className="text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">More than just a workplace. It's a playground for innovation.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="group rounded-3xl overflow-hidden glass-panel-intense border border-slate-200 dark:border-slate-700 hover:-translate-y-2 transition-all duration-500 shadow-xl">
                    <div className="relative h-64 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent z-10"></div>
                        <Image alt="Remote Working" className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700" width={400} height={256} src="https://lh3.googleusercontent.com/aida-public/AB6AXuBS4av19AAysNknF5nwQATzOnDezxzn-xDpqcC7-CcHQ1nYMz6E2erM7q0o5RVxQV0II3_y4tD2RnsYx4HDQVrbG2NdRLTenTFhwptb4GjdQSaXe8MkLk338rE0awX_ga2MUqPKPViiEDw7rDzx4Ray5sSKF48O5Z2RDvKFeHpJpo8qIMNrC4Qx4fxUBUjQl2Hyer-0wsxTOMUigEkVONZ8PYwj_HoQJTpilagbSxYqL8X6kY69c3kLWrP-BrsyvKGg_LvWPqq69yI"/>
                        <div className="absolute bottom-4 left-6 z-20">
                            <Globe className="text-4xl text-neon-blue mb-2" />
                            <h3 className="text-2xl font-bold text-white">Remote First</h3>
                        </div>
                    </div>
                    <div className="p-6">
                        <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                            Work from anywhere in the world. We believe in output over hours and trust our team to deliver excellence from their preferred environment.
                        </p>
                    </div>
                </div>
                <div className="group rounded-3xl overflow-hidden glass-panel-intense border border-slate-200 dark:border-slate-700 hover:-translate-y-2 transition-all duration-500 shadow-xl">
                    <div className="relative h-64 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent z-10"></div>
                        <Image alt="Learning" className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700" width={400} height={256} src="https://lh3.googleusercontent.com/aida-public/AB6AXuCvY9E862eT0ExxdwsK5zbFclS1oWmD9lnu4RUvFPy0RoFQcghg9ldVwwEGKR7w69mZLY559dKGwvU6Rj5esSS05Qs2Xm6Qrir2_6PXHdTXMQVijTZpJlIhyw3JNSLY1wIaCuLaMKNfqI8BMdxw4hhK-reBL7hTr6gNXishmREtGqkiv9lRAnEqyFVk7aWulj94vq_6ess5cwn0o3M4QyPd5349O1rgLcS7IO4amwTIHuvzVWl6jmAa3kgo88zh0SkX7OmNSOqcUx0"/>
                        <div className="absolute bottom-4 left-6 z-20">
                            <TrendingUp className="text-4xl text-neon-green mb-2" />
                            <h3 className="text-2xl font-bold text-white">Learning & Growth</h3>
                        </div>
                    </div>
                    <div className="p-6">
                        <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                            Unlimited access to courses, conferences, and certifications. We invest heavily in your upskilling because when you grow, we grow.
                        </p>
                    </div>
                </div>
                <div className="group rounded-3xl overflow-hidden glass-panel-intense border border-slate-200 dark:border-slate-700 hover:-translate-y-2 transition-all duration-500 shadow-xl">
                    <div className="relative h-64 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent z-10"></div>
                        <Image alt="Innovation" className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700" width={400} height={256} src="https://lh3.googleusercontent.com/aida-public/AB6AXuCLyzY8jpM0qd-8Ef4n4LWKWlrOhO8TUEvDzvjm_-sW13CSFq2BvPxwsMf98xAyCuXrR2z7JL2TrhM87ckE3W043cRrvEvbKT4DzYDhKDcSqFrwxMCd-bQ-oiaUT_bSyAzT4GvQCdGOjQXrG9y91J1Bex4AeHpy8zIPJw-JJknwpEkUcuBOKpgCqjhTGEaO5oueJSYi5uSUZHqjvRB44pZ6R7XsJoHvEoipYwsER5Ft9Z0WBwfniTKa9ojMbkEMGI2YxQ8Q1YHinBg"/>
                        <div className="absolute bottom-4 left-6 z-20">
                            <Lightbulb className="text-4xl text-neon-purple mb-2" />
                            <h3 className="text-2xl font-bold text-white">Innovative Projects</h3>
                        </div>
                    </div>
                    <div className="p-6">
                        <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                            Say goodbye to boring legacy maintenance. Work on cutting-edge AI, Blockchain, and IoT projects that challenge the status quo.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </section>
  );
}

// Section 3: Perks
function Perks() {
    return (
        <section className="py-20 relative bg-slate-50 dark:bg-[#0a0f1c] overflow-hidden">
            <div className="absolute right-0 top-0 w-1/2 h-full bg-gradient-to-l from-blue-500/5 to-transparent pointer-events-none"></div>
            <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
                <div className="flex flex-col md:flex-row items-center justify-between gap-12">
                    <div className="w-full md:w-1/3">
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-white mb-6">Perks &amp; <span className="text-secondary">Benefits</span></h2>
                        <p className="text-slate-600 dark:text-slate-400 mb-8">We take care of the people who build our success. Enjoy a comprehensive benefits package designed for your well-being.</p>
                        <div className="hidden md:block w-32 h-32 bg-secondary/20 rounded-full blur-3xl animate-pulse-slow"></div>
                    </div>
                    <div className="w-full md:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div className="p-6 rounded-2xl glass-panel-intense neon-border-blue border border-slate-200 dark:border-slate-700 flex items-center gap-4 group cursor-default">
                            <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
                                <Clock className="text-primary text-2xl group-hover:scale-110 transition-transform" />
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-800 dark:text-white">Flexible Hours</h4>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Work when you are most productive</p>
                            </div>
                        </div>
                        <div className="p-6 rounded-2xl glass-panel-intense neon-border-green border border-slate-200 dark:border-slate-700 flex items-center gap-4 group cursor-default">
                            <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center group-hover:bg-green-500/20 transition-colors">
                                <Handshake className="text-secondary text-2xl group-hover:scale-110 transition-transform" />
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-800 dark:text-white">Health Insurance</h4>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Comprehensive coverage for you &amp; family</p>
                            </div>
                        </div>
                        <div className="p-6 rounded-2xl glass-panel-intense neon-border-purple border border-slate-200 dark:border-slate-700 flex items-center gap-4 group cursor-default">
                            <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center group-hover:bg-purple-500/20 transition-colors">
                                <Plane className="text-accent text-2xl group-hover:scale-110 transition-transform" />
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-800 dark:text-white">Annual Retreats</h4>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Exotic locations for team bonding</p>
                            </div>
                        </div>
                        <div className="p-6 rounded-2xl glass-panel-intense neon-border-orange border border-slate-200 dark:border-slate-700 flex items-center gap-4 group cursor-default">
                            <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center group-hover:bg-orange-500/20 transition-colors">
                                <Award className="text-orange-500 text-2xl group-hover:scale-110 transition-transform" />
                            </div>
                            <div>
                                <h4 className="font-bold text-slate-800 dark:text-white">Performance Bonuses</h4>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Rewarding your hard work and impact</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

// Section 4: Openings
function Openings() {
    const [openIndex, setOpenIndex] = useState<number | null>(0);
    const [searchQuery, setSearchQuery] = useState('');

    const handleToggle = (index: number) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    const filteredOpenings = openingsData.filter(job => {
        const query = searchQuery.toLowerCase();
        return (
            job.title.toLowerCase().includes(query) ||
            job.location.toLowerCase().includes(query) ||
            job.category.toLowerCase().includes(query) ||
            job.requirements.some(req => req.toLowerCase().includes(query)) ||
            job.benefits.some(ben => ben.toLowerCase().includes(query))
        );
    });

    return (
        <section className="py-24 bg-background relative overflow-hidden" id="openings">
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.03]"></div>
            <div className="max-w-5xl mx-auto px-6 md:px-12 relative z-10">
                <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-5xl font-bold text-slate-800 dark:text-white mb-4">Open <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-purple to-neon-blue">Positions</span></h2>
                    <p className="text-slate-500 dark:text-slate-400">Find the role that fits your superpower.</p>
                </div>
                <div className="relative max-w-2xl mx-auto mb-16">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Search className="text-slate-400" />
                    </div>
                    <input 
                        className="w-full pl-12 pr-4 py-4 bg-white/5 backdrop-blur-md border border-slate-200 dark:border-slate-700 rounded-2xl focus:ring-2 focus:ring-primary focus:border-transparent text-slate-800 dark:text-white shadow-lg placeholder-slate-400 transition-all" 
                        placeholder="Search by role, keyword, or location..." 
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <div className="absolute inset-y-0 right-2 flex items-center">
                        <button className="bg-primary text-white px-4 py-1.5 rounded-xl text-sm font-medium hover:bg-blue-600 transition-colors">Find Job</button>
                    </div>
                </div>
                <div className="space-y-6">
                    {filteredOpenings.map((job, index) => (
                        <div key={index} className={cn("group glass-panel-intense rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden transition-all duration-300", openIndex === index ? job.borderColor : `hover:${job.borderColor}`)}>
                            <div onClick={() => handleToggle(index)} className="p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4 cursor-pointer relative z-10">
                                <div>
                                    <h3 className={cn("text-xl md:text-2xl font-bold text-slate-800 dark:text-white transition-colors", openIndex === index ? `text-${job.color}-500` : `group-hover:text-${job.color}-500`)}>
                                        {job.title}
                                    </h3>
                                    <div className="flex flex-wrap gap-3 mt-2 text-sm text-slate-500 dark:text-slate-400">
                                        <span className="flex items-center gap-1"><MapPin className="text-base" /> {job.location}</span>
                                        <span className="flex items-center gap-1"><Clock className="text-base" /> {job.type}</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className={cn("px-3 py-1 rounded-full text-xs font-bold", job.categoryBg, job.categoryColor, job.categoryBorder)}>{job.category}</span>
                                    <ChevronRight className={cn("h-6 w-6 text-slate-400 transition-transform duration-300", { 'rotate-90': openIndex === index })} />
                                </div>
                            </div>
                            <div className={cn("max-h-0 overflow-hidden transition-all duration-500 ease-in-out bg-slate-50/50 dark:bg-slate-900/50", { 'max-h-[500px]': openIndex === index })}>
                                <div className="p-6 md:p-8 pt-0 border-t border-slate-200/50 dark:border-slate-700/50 mt-2">
                                    {(job.requirements.length > 0 || job.benefits.length > 0) && (
                                        <div className="grid md:grid-cols-2 gap-6 py-4 text-sm text-slate-600 dark:text-slate-300">
                                            {job.requirements.length > 0 && <div>
                                                <strong className="block text-slate-900 dark:text-white mb-2">Requirements:</strong>
                                                <ul className="list-disc pl-5 space-y-1 marker:text-primary">
                                                    {job.requirements.map((req, i) => <li key={i}>{req}</li>)}
                                                </ul>
                                            </div>}
                                            {job.benefits.length > 0 && <div>
                                                <strong className="block text-slate-900 dark:text-white mb-2">Benefits:</strong>
                                                <ul className="list-disc pl-5 space-y-1 marker:text-secondary">
                                                    {job.benefits.map((ben, i) => <li key={i}>{ben}</li>)}
                                                </ul>
                                            </div>}
                                        </div>
                                    )}
                                    <div className="flex justify-end mt-4">
                                        <Link href={`/contact?inquiry=job&source=careers&title=${encodeURIComponent(job.title)}`}>
                                            <button className={cn("text-white px-8 py-3 rounded-full font-bold transition-all transform active:scale-95 flex items-center", 
                                            { 
                                                'bg-primary hover:bg-blue-600': job.color === 'blue',
                                                'bg-accent hover:bg-violet-600': job.color === 'purple',
                                                'bg-secondary hover:bg-emerald-600': job.color === 'green',
                                                'bg-orange-500 hover:bg-orange-600': job.color === 'orange',
                                                'bg-red-500 hover:bg-red-600': job.color === 'red',
                                                'bg-indigo-500 hover:bg-indigo-600': job.color === 'indigo',
                                            })}>
                                                Apply Now
                                            </button>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// Section 5: CTA
function CTA() {
    return (
        <section className="py-24 relative overflow-hidden flex items-center justify-center">
            <div className="absolute inset-0 bg-dark-navy z-0">
                <div className="absolute inset-0 liquid-bg opacity-30"></div>
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-20"></div>
            </div>
            <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
                <h2 className="text-4xl md:text-6xl font-extrabold text-white mb-8 tracking-tight drop-shadow-lg">
                    Don't see a <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-green to-neon-blue">Fit?</span>
                </h2>
                <p className="text-xl text-slate-200 mb-10 max-w-2xl mx-auto">
                    We are always looking for exceptional talent. Send us your resume and we'll keep you on our radar for future opportunities.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <Link href="/contact?inquiry=job&source=careers">
                        <button className="px-10 py-5 bg-white text-primary rounded-full font-bold text-lg transition-all duration-300 hover:scale-105 flex items-center justify-center group">
                            Send Your Resume
                        </button>
                    </Link>
                </div>
            </div>
        </section>
    )
}

// Main Page Component
export default function CareersPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main>
        <Hero />
        <Life />
        <Perks />
        <Openings />
        <CTA />
      </main>
      <Footer />
    </div>
  );
}
